<script setup lang="ts">
/**
 * RelatedNews — 입시 소식 상세 하단 "다른 입시 소식"(083 FR-011): 최근 글 최대 3건(현재 글 제외) + 목록 이동.
 * 별도 API 없이 목록 API 첫 페이지(크기 4)를 재사용한다 — 현재 글이 섞여 있어도 3건이 남는다.
 * 보조 블록이라 실패·빈 결과는 조용히 목록 이동만 남긴다(본문 열람을 방해하지 않음).
 */
import { useQuery } from '@tanstack/vue-query';
import { ChevronRight } from 'lucide-vue-next';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { fetchNewsList } from '#/api/news';
import { buttonVariants } from '#/components/ui';
import { formatKstDate } from '#/lib/datetime';
import { publicQueryKeys, RELATED_NEWS_FETCH_SIZE } from '#/queries/publicQueries';

const RELATED_MAX = 3;

const props = defineProps<{
  /** 현재 보고 있는 글 — 목록에서 제외한다. */
  currentId: number;
}>();

const { data } = useQuery({
  queryKey: publicQueryKeys.newsList(0, RELATED_NEWS_FETCH_SIZE),
  queryFn: () => fetchNewsList(0, RELATED_NEWS_FETCH_SIZE),
});

const items = computed(() =>
  (data.value?.content ?? []).filter((n) => n.id !== props.currentId).slice(0, RELATED_MAX),
);
</script>

<template>
  <section class="mt-8" aria-labelledby="related-news-title">
    <h2 id="related-news-title" class="mb-3 text-[15px] font-extrabold text-brand">
      다른 입시 소식
    </h2>
    <ul
      v-if="items.length > 0"
      class="divide-y divide-line rounded-card border border-line bg-white"
    >
      <li v-for="item in items" :key="item.id">
        <RouterLink
          :to="`/news/${item.id}/`"
          class="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-bg-2"
        >
          <p class="min-w-0 flex-1 truncate text-[14px] font-bold text-brand">{{ item.title }}</p>
          <span class="shrink-0 text-[12px] text-muted">{{ formatKstDate(item.publishedAt) }}</span>
        </RouterLink>
      </li>
    </ul>
    <div class="mt-4 flex justify-center">
      <RouterLink to="/news/" :class="buttonVariants({ variant: 'line', size: 'md' })">
        입시 소식 전체 보기
        <ChevronRight class="h-4 w-4" />
      </RouterLink>
    </div>
  </section>
</template>
